// src/pages/problem-bank/AnalystPerformanceProfile.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ProblemBankEntry } from '@/types/problemBank';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  CalendarDays,
  Activity,
  Target,
  CheckCircle,
  Star,
  Award,
  BarChart3,
  Eye
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

// Load employee profile data from localStorage
const getEmployeeProfileFromStorage = (email: string): any => {
  const saved = localStorage.getItem(`employee_${email}`);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const DEFAULT_PROFILE_IMAGES: Record<string, string> = {
  'admin@vettandventure.com': 'https://images.pexels.com/photos/3778876/pexels-photo-3778876.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'analyst@vett.com': 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'analyst2@vett.com': 'https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'senior@vett.com': 'https://images.pexels.com/photos/697509/pexels-photo-697509.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop'
};

const DEFAULT_JOB_TITLES: Record<string, string> = {
  'admin@vettandventure.com': 'System Administrator',
  'analyst@vett.com': 'Investment Analyst',
  'analyst2@vett.com': 'Climate Tech Analyst',
  'senior@vett.com': 'Senior Investment Analyst'
};

const DEFAULT_SPECIALIZATIONS: Record<string, string[]> = {
  'admin@vettandventure.com': ['Venture Capital', 'Portfolio Management', 'Strategy'],
  'analyst@vett.com': ['FinTech', 'SaaS', 'Financial Modeling'],
  'analyst2@vett.com': ['Climate Tech', 'Impact Investing', 'ESG'],
  'senior@vett.com': ['Due Diligence', 'Deal Structuring', 'Mentorship']
};

const getPerformanceColor = (value: number) => {
  if (value >= 85) return 'text-green-600';
  if (value >= 70) return 'text-blue-600';
  if (value >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const calculateProgress = (problem: any): number => {
  if (!problem.assignment?.milestones) return 0;
  const completed = problem.assignment.milestones.filter((m: any) => m.completed).length;
  return Math.round((completed / problem.assignment.milestones.length) * 100);
};

export default function AnalystPerformanceProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allUsers } = useAuth();
  const { toast } = useToast();
  
  const [analyst, setAnalyst] = useState<any>(null);
  const [problems, setProblems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadAnalystData();
  }, [id]);

  const loadAnalystData = async () => {
    setIsLoading(true);
    try {
      // Find the analyst in allUsers
      const analystUser = allUsers.find(u => u.id === id);
      
      if (!analystUser) {
        toast({
          title: "Analyst not found",
          description: "The analyst you're looking for doesn't exist.",
          variant: "destructive"
        });
        navigate('/admin/assignments');
        return;
      }

      // Load problems from Supabase
      const { data: problemsData, error: problemsError } = await supabase
        .from('problem_bank')
        .select('*')
        .order('created_at', { ascending: false });

      if (problemsError) throw problemsError;

      // Parse JSON fields for each problem
      const parsedProblems = (problemsData || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        outcome: typeof problem.outcome === 'string' ? JSON.parse(problem.outcome) : problem.outcome,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id
      }));

      setProblems(parsedProblems);

      // Get profile from localStorage
      const employeeData = getEmployeeProfileFromStorage(analystUser.email);
      const profileImage = employeeData?.imageUrl || DEFAULT_PROFILE_IMAGES[analystUser.email] || null;
      
      // Find problems assigned to this analyst
      const assignedProblems = parsedProblems.filter(p => 
        p.status === 'assigned' && 
        p.assignment?.members?.some((m: any) => m.userId === analystUser.id)
      );
      const completedProblems = parsedProblems.filter(p => 
        p.status === 'solved' && 
        p.assignment?.members?.some((m: any) => m.userId === analystUser.id)
      );
      
      // Calculate completion times
      const completionTimes = completedProblems.map(p => {
        if (p.assignment?.assignmentDate && p.outcome?.deliveryDate) {
          const assigned = new Date(p.assignment.assignmentDate);
          const completed = new Date(p.outcome.deliveryDate);
          return Math.ceil((completed.getTime() - assigned.getTime()) / (1000 * 60 * 60 * 24));
        }
        return 0;
      }).filter(days => days > 0);
      
      const avgCompletionDays = completionTimes.length > 0 
        ? Math.round(completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length)
        : 0;
      
      const onTimeCount = completedProblems.filter(p => {
        if (p.assignment?.targetDeliveryDate && p.outcome?.deliveryDate) {
          return new Date(p.outcome.deliveryDate) <= new Date(p.assignment.targetDeliveryDate);
        }
        return false;
      }).length;
      
      const onTimeRate = completedProblems.length > 0 
        ? Math.round((onTimeCount / completedProblems.length) * 100)
        : 0;
      
      const avgScore = completedProblems.length > 0 
        ? Math.round(completedProblems.reduce((sum, p) => sum + (p.priorityScore?.totalScore || 0), 0) / completedProblems.length * 20)
        : 75;
      
      const problemDetails = parsedProblems
        .filter(p => p.assignment?.members?.some((m: any) => m.userId === analystUser.id))
        .map(p => ({
          id: p.id,
          title: p.title,
          status: p.status,
          assignedAt: p.assignment?.assignmentDate || '',
          targetDate: p.assignment?.targetDeliveryDate || '',
          completedAt: p.outcome?.deliveryDate,
          progress: p.status === 'assigned' ? calculateProgress(p) : 100,
          score: p.priorityScore?.totalScore,
          priorityTier: p.priorityScore?.priorityTier || 'medium',
          companyName: p.anchor?.name || 'Unknown',
          lossPerYear: p.quantification?.totalLossPerYear || 0
        }));
      
      setAnalyst({
        id: analystUser.id,
        name: analystUser.name,
        email: analystUser.email,
        phone: employeeData?.phone || '+1 (555) 000-0000',
        role: analystUser.role,
        avatar: profileImage,
        title: employeeData?.title || DEFAULT_JOB_TITLES[analystUser.email] || (analystUser.role === 'senior_analyst' ? 'Senior Investment Analyst' : 'Investment Analyst'),
        department: employeeData?.department || 'Investment Analysis',
        joinDate: employeeData?.startDate || 'January 2024',
        education: employeeData?.education || 'MBA, Finance',
        yearsExperience: employeeData?.yearsExperience || 5,
        specialization: employeeData?.specialization || DEFAULT_SPECIALIZATIONS[analystUser.email] || ['Financial Analysis', 'Due Diligence'],
        currentLoad: assignedProblems.length,
        completedCount: completedProblems.length,
        avgScore,
        avgCompletionDays,
        onTimeRate,
        problems: problemDetails
      });

    } catch (error) {
      console.error('Failed to load analyst data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analyst profile',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background px-4 md:px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-10 w-32 mb-6" />
          <Skeleton className="h-64 w-full rounded-lg mb-6" />
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!analyst) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-12 pb-12 text-center">
            <p className="text-muted-foreground">Analyst not found</p>
            <Button className="mt-4" onClick={() => navigate('/admin/assignments')}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Header with Navigation */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/assignments')} 
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-purple-100 text-purple-700">
              Analyst Performance Profile
            </Badge>
            <Badge variant="outline">{analyst.role === 'senior_analyst' ? 'Senior Analyst' : 'Analyst'}</Badge>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-6 max-w-7xl mx-auto">
        {/* Profile Header Card */}
        <Card className="overflow-hidden mb-6">
          <div className="h-32 bg-gradient-to-r from-blue-600 to-purple-600" />
          <CardContent className="relative pt-0">
            <div className="flex flex-col md:flex-row gap-6 -mt-16">
              <div className="flex-shrink-0">
                <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white">
                  {analyst.avatar ? (
                    <img 
                      src={analyst.avatar}
                      alt={analyst.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-3xl font-bold bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                      {getInitials(analyst.name)}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex-1 pt-4">
                <h1 className="text-3xl font-bold">{analyst.name}</h1>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className="bg-blue-100 text-blue-800">{analyst.title}</Badge>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Target className="h-4 w-4" />
                    Problem Solver Profile
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  View all problems assigned to this analyst and track their progress across milestones
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium truncate">{analyst.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{analyst.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarDays className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Joined Team</p>
                      <p className="font-medium">{analyst.joinDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Activity className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Experience</p>
                      <p className="font-medium">{analyst.yearsExperience} years</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-muted-foreground">{analyst.education}</p>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {analyst.specialization.map((spec: string, i: number) => (
                    <Badge key={i} variant="secondary">{spec}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold">{analyst.currentLoad}</p>
                <p className="text-sm text-muted-foreground">Active Problems</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{analyst.completedCount}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className={`text-3xl font-bold ${getPerformanceColor(analyst.avgScore)}`}>{analyst.avgScore}</p>
                <p className="text-sm text-muted-foreground">Quality Score</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className={`text-3xl font-bold ${getPerformanceColor(analyst.onTimeRate)}`}>{analyst.onTimeRate}%</p>
                <p className="text-sm text-muted-foreground">On-Time Rate</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Problems */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="active">
              Active Problems ({analyst.problems.filter((p: any) => p.status === 'assigned').length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({analyst.problems.filter((p: any) => p.status === 'solved').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="space-y-4">
            {analyst.problems.filter((p: any) => p.status === 'assigned').map((problem: any) => {
              const daysRemaining = (() => {
                const target = new Date(problem.targetDate);
                const now = new Date();
                const diffTime = target.getTime() - now.getTime();
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              })();
              const isOverdue = daysRemaining < 0;
              
              return (
                <Card 
                  key={problem.id} 
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => navigate(`/problem-bank/problem/${problem.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold">{problem.title}</h3>
                        <p className="text-sm text-muted-foreground">{problem.companyName}</p>
                      </div>
                      <Badge className={problem.priorityTier === 'critical' ? 'bg-red-100 text-red-700' : problem.priorityTier === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}>
                        {problem.priorityTier.toUpperCase()}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                      <div>
                        <p className="text-muted-foreground">Assigned</p>
                        <p className="font-medium">{new Date(problem.assignedAt).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Target Delivery</p>
                        <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
                          {new Date(problem.targetDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Days Remaining</p>
                        <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
                          {daysRemaining > 0 ? `${daysRemaining} days` : `${Math.abs(daysRemaining)} days overdue`}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Progress</span>
                        <span className="font-medium">{problem.progress}%</span>
                      </div>
                      <Progress value={problem.progress} className="h-2" />
                    </div>
                    
                    <div className="mt-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/problem-bank/problem/${problem.id}`);
                        }}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {analyst.problems.filter((p: any) => p.status === 'assigned').length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No active problems</p>
                  <p className="text-sm text-muted-foreground mt-1">{analyst.name} is currently up to date</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {analyst.problems.filter((p: any) => p.status === 'solved').map((problem: any) => (
              <Card 
                key={problem.id} 
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => navigate(`/problem-bank/problem/${problem.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold">{problem.title}</h3>
                      <p className="text-sm text-muted-foreground">{problem.companyName}</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">SOLVED</Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Assigned</p>
                      <p className="font-medium">{new Date(problem.assignedAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Completed</p>
                      <p className="font-medium text-green-600">{problem.completedAt ? new Date(problem.completedAt).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    {problem.score && (
                      <div>
                        <p className="text-muted-foreground">Quality Score</p>
                        <p className="font-medium">{problem.score}/5.0</p>
                      </div>
                    )}
                  </div>
                  
                  {problem.score && problem.score >= 4 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 p-2 rounded-lg">
                      <Star className="h-4 w-4" />
                      Excellent performance on this problem
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {analyst.problems.filter((p: any) => p.status === 'solved').length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Award className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No completed problems yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Completed problems will appear here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}